import React, { useEffect, useMemo, useRef, useState } from "react";

import { useNotifications } from "@/hooks/useNotifications";
import { NotificationPanel } from "./NotificationPanel";
import { NotificationsModal } from "./NotificationsModal";
import type { Notification } from "@/types/notifications";

interface NotificationBellProps {
  /** Filters to a specific client when the user is in a client view.
   *  Pass null/undefined on agency-level pages to see everything. */
  clientId?: string | null;
  /** Optional click handler — typically routes the user to the
   *  notification's source (post, campaign, etc.). */
  onItemClick?: (n: Notification) => void;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({
  clientId,
  onItemClick,
}) => {
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const {
    notifications,
    unreadCount,
    isLoading,
    markRead,
    markAllRead,
  } = useNotifications(clientId);

  const unreadNotifications = useMemo(
    () => notifications.filter((n) => n.readAt === null),
    [notifications],
  );

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
        aria-label="Notifications"
        title="Notifications"
      >
        <BellIcon className="w-5 h-5" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center"
            aria-label={`${unreadCount} unread`}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <NotificationPanel
          notifications={unreadNotifications}
          isLoading={isLoading}
          unreadCount={unreadCount}
          totalCount={notifications.length}
          onMarkRead={markRead}
          onMarkAllRead={markAllRead}
          onItemClick={(n) => {
            onItemClick?.(n);
            setOpen(false);
          }}
          onViewAll={() => {
            setOpen(false);
            setModalOpen(true);
          }}
          onClose={() => setOpen(false)}
        />
      )}

      <NotificationsModal
        isOpen={modalOpen}
        notifications={notifications}
        isLoading={isLoading}
        unreadCount={unreadCount}
        onMarkRead={markRead}
        onMarkAllRead={markAllRead}
        onItemClick={
          onItemClick
            ? (n) => {
                onItemClick(n);
                setModalOpen(false);
              }
            : undefined
        }
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
};

const BellIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
    />
  </svg>
);
